import Span from "@/components/ui/Span";
import { formatDistance } from "@/utils/helpers";
import { Rating } from "@mui/material";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import StarIcon from "@mui/icons-material/Star";
import { useTheme } from "next-themes";
import { usePlaceViewNavigation } from "@/hooks/usePlaceViewNavigation";
import { getMediaRecordUrl, getPlaceFallbackMediaRecords } from "@/utils/media";
import { getPlaceIdentifier } from "@/utils/placeUtils";
import Iconify from "@/components/ui/Iconify";
import { ListTile, TileRating } from "@/components/kit";

const tileMediaFrameClass =
  "min-w-24 w-24 h-32 rounded-md bg-gray-100 dark:bg-heading_black flex items-center justify-center border dark:border-slate overflow-hidden relative";

const PlaceTileMediaFallback = ({ className = tileMediaFrameClass }) => (
  <div
    className={`${className} border-dashed border-black/10 bg-black/[0.03] text-black/25 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/25`}
    aria-hidden="true"
  >
    <span className="relative block h-8 w-8 rounded-md border border-current">
      <span className="absolute bottom-1 left-1 block h-2 w-5 rounded-sm bg-current opacity-25" />
      <span className="absolute right-1 top-1 block h-1.5 w-1.5 rounded-full bg-current opacity-40" />
    </span>
  </div>
);

const getHotpleStatsLabel = (place) => {
  const stats = place?.hotpleStats;
  if (!stats || Number(stats.videoCount) <= 0) return "";

  const parts = [`태그 영상 ${stats.videoCount}개`];
  if (Number(stats.uniqueUploaderCount) > 0) {
    parts.push(`업로더 ${stats.uniqueUploaderCount}명`);
  }
  if (Number(stats.recentVideoCount) > 0) {
    parts.push(`최근 ${stats.recentWindowDays || 90}일 ${stats.recentVideoCount}개`);
  }

  return parts.join(" · ");
};

const toCount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const number = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const getReviewCount = (place) =>
  Math.max(
    toCount(place?.totalReviews),
    toCount(place?.totalDbReviews),
    toCount(place?.numberOfVisitorReviews),
    toCount(place?.numberOfBlogReviews)
  );

const formatCount = (value) => new Intl.NumberFormat("ko-KR").format(value || 0);

const formatRating = (value) => {
  const rating = Number(value || 0);
  if (!Number.isFinite(rating) || rating <= 0) return "";
  return rating.toFixed(1);
};

const getHotpleReviewCount = (place) =>
  Math.max(toCount(place?.hotpleReviewCount), toCount(place?.hotpleRatingCount));

const getHotpleAverageRating = (place) => {
  const rating = Number(place?.hotpleAverageRating || 0);
  return Number.isFinite(rating) && rating > 0 ? rating : 0;
};

const getExternalRating = (place) => {
  const rating = Number(
    place?.naverRating ?? place?.nRating ?? place?.averageRating ?? 0
  );
  return Number.isFinite(rating) && rating > 0 ? rating : 0;
};

const getPlayCount = (place) =>
  Math.max(
    toCount(place?.hotpleStats?.videoCount),
    toCount(place?.hotpleStats?.effectiveVideoCount),
    toCount(place?.hotpleStats?.recentVideoCount)
  );

const getSaveCount = (place) =>
  Math.max(
    toCount(place?.savedCount),
    toCount(place?.saveCount),
    toCount(place?.bookmarkCount),
    toCount(place?.favoriteCount),
    toCount(place?.totalSaves),
    toCount(place?.totalBookmarks)
  );

const PlaceTile = ({
  i,
  place,
  isMobile,
  isHotpleLanding = false,
  handlePlaceListTileClick = () => {},
}) => {
  const { theme } = useTheme();
  const { searchParams, routeToPlaceDetail, routeToPlaceMap } =
    usePlaceViewNavigation();
  const tileRef = useRef(null);

  const searchPlace = searchParams?.get("search-place");
  const viewPlaceId = searchParams.get("view-place");
  const placeId =
    getPlaceIdentifier(place) ||
    place?.placeUniqueNumber ||
    place?.contentid ||
    place?.contentId ||
    place?.publicId ||
    place?._id ||
    place?.id;
  const isSelected = placeId && String(viewPlaceId) === String(placeId);

  const [placeMediaLoader, setPlaceMediaLoader] = useState(false);

  const [placeMedia, setPlaceMedia] = useState(() =>
    Array.isArray(place?.placeMedia) ? place.placeMedia : []
  );
  const [placeReviewText, setPlaceReviewText] = useState(
    place?.placeReviewText || null
  );
  const [tileImageError, setTileImageError] = useState(false);
  const loadMediaImmediately = i < (isMobile ? 5 : 6);
  // Always render the <Image>; let next/image's NATIVE lazy loading defer the
  // fetch for below-the-fold tiles. The old custom IntersectionObserver gate
  // (shouldRenderMedia starting false for i>=5) left tiles permanently blank
  // whenever the observer failed to fire inside the transformed bottom-sheet —
  // the real cause of "photo shows in detail but the list tile stays empty".
  const [shouldRenderMedia, setShouldRenderMedia] = useState(true);

  // 핫플 장소는 사진을 `placeMedia` 에 갖고, 예전에 들여온 장소는
  // `firstimage`/`firstimage2`. Fall back to the VK images so tourist-spot tiles
  // don't render as blank placeholders next to hotple tiles that do have photos.
  const effectiveMedia =
    placeMedia?.length > 0 ? placeMedia : getPlaceFallbackMediaRecords(place);

  const focusPlaceOnMap = () => {
    if (!placeId) return;
    routeToPlaceMap(place, { afterOpen: handlePlaceListTileClick });
  };

  const handleTileClick = () => {
    if (!placeId) return;

    if (isMobile || isHotpleLanding) {
      routeToPlaceDetail(place);
      return;
    }

    focusPlaceOnMap();
  };

  function renderHighlightedText(renderText, matchText, className) {
    const matchChars = new Set(matchText.toLowerCase());
    const highlightedText = renderText?.split("")?.map((char, index) => {
      const isMatch = matchChars.has(char.toLowerCase());
      return (
        <span
          key={index}
          style={{
            fontWeight: isMatch ? "bold" : "normal",
            color: isMatch ? "red" : "inherit",
          }}
        >
          {char}
        </span>
      );
    });
    return <span className={className}>{highlightedText}</span>;
  }

  useEffect(() => {
    setPlaceMedia(Array.isArray(place?.placeMedia) ? place.placeMedia : []);
    setPlaceReviewText(place?.placeReviewText || null);
    setPlaceMediaLoader(false);
    setTileImageError(false);
    setShouldRenderMedia(true);
  }, [loadMediaImmediately, placeId, place?.placeMedia, place?.placeReviewText]);

  useEffect(() => {
    if (loadMediaImmediately || shouldRenderMedia || effectiveMedia?.length === 0) {
      return;
    }

    const node = tileRef.current;
    if (!node || typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) {
      setShouldRenderMedia(true);
      return;
    }

    const rootElement = document.getElementById(
      isMobile ? "scrollableDiv-mobile" : "scrollableDiv"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setShouldRenderMedia(true);
        observer.disconnect();
      },
      {
        root: rootElement?.contains(node) ? rootElement : null,
        rootMargin: "480px 0px",
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    i,
    isMobile,
    loadMediaImmediately,
    placeId,
    effectiveMedia?.length,
    shouldRenderMedia,
  ]);

  const hasPlaceMedia = effectiveMedia?.length > 0;
  const tileImageSrc = hasPlaceMedia
    ? getMediaRecordUrl(effectiveMedia?.[0], {
        variant: "thumbnail",
        preferPlaceholder: true,
      })
    : "";
  const shouldShowTileImage =
    hasPlaceMedia && shouldRenderMedia && tileImageSrc && !tileImageError;
  const closureRisk = place?.localdataClosureRisk;
  const closureRiskLabel = closureRisk?.publicWarning
    ? closureRisk.label || "\uC601\uC5C5 \uD655\uC778 \uD544\uC694"
    : "";
  const closureRiskClass =
    closureRisk?.level === "CONFIRMED_CLOSED"
      ? "border-red-300 bg-red-50 text-red-700 dark:border-red-400/40 dark:bg-red-400/10 dark:text-red-200"
      : "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-100";
  const hotpleStatsLabel = getHotpleStatsLabel(place);
  const reviewCount = getReviewCount(place);
  const hotpleReviewCount = getHotpleReviewCount(place);
  const hotpleAverageRating = getHotpleAverageRating(place);
  const externalRating = getExternalRating(place);
  const ratingValue =
    isHotpleLanding && hotpleAverageRating > 0
      ? hotpleAverageRating
      : Number(place?.averageDbRating || externalRating || 0);
  const ratingLabel = isHotpleLanding
    ? hotpleAverageRating > 0
      ? `Hotple 평점 ${formatRating(hotpleAverageRating)}`
      : externalRating > 0
        ? `방문 평점 ${formatRating(externalRating)}`
        : "평점 대기"
    : "";
  const playCount = getPlayCount(place);
  const saveCount = getSaveCount(place);
  const displayReviewCount = isHotpleLanding
    ? reviewCount
    : Number(place?.totalDbReviews ?? place?.numberOfVisitorReviews ?? 0);
  const tileLayoutClass = isHotpleLanding
    ? "mx-2 my-2 max-w-[calc(100%-1rem)] rounded-md border border-black/10 px-3 py-3 shadow-sm dark:border-white/10"
    : "w-full max-w-full px-4 py-5 border-b dark:border-b-brown";
  const tileStateClass = isSelected
    ? "bg-black/10"
    : isHotpleLanding
      ? "bg-white hover:bg-black/[0.03] dark:bg-custom_black dark:hover:bg-white/[0.05]"
      : "bg-transparent hover:bg-black/5";
  const titleClassName = isHotpleLanding
    ? "font-bold text-[15px] leading-snug tracking-normal transition break-words"
    : "font-semibold text-base md:text-lg tracking-tight transition";
  const mediaFrameClass = isHotpleLanding
    ? "min-w-[96px] w-[96px] h-[128px] rounded-md bg-gray-100 dark:bg-heading_black flex items-center justify-center border dark:border-slate overflow-hidden relative"
    : tileMediaFrameClass;
  const placeMeta = [
    place?.type,
    place?.subcategory,
    place?.address || place?.old_address,
  ]
    .filter(Boolean)
    .join(" · ");
  const reviewSnippet =
    placeReviewText ||
    place?.reviewSummary ||
    place?.featuredReview ||
    place?.hotpleStats?.featuredPost?.description ||
    "방문 후기와 Play 반응을 상세 페이지에서 확인해보세요.";
  const handleDetailActionClick = (event) => {
    event.stopPropagation();
    routeToPlaceDetail(place);
  };

  const handleMapActionClick = (event) => {
    event.stopPropagation();
    focusPlaceOnMap();
  };

  // 이름 앞 번호(1. 2. …)는 뗐다. 지도 핀에는 번호가 없어 짝이 안 맞았고,
  // 짝이 없으면 번호가 순위처럼 읽힌다 — 실제로는 그냥 나열 순서다.
  // 앱 목록(지역 랜딩 카드가 아닌 경우)은 공용 타일을 쓴다.
  //
  // 예전에는 이 화면만 사진이 오른쪽에 있고 구분자도 "," 와 "|" 를 섞어 써서,
  // 같은 가게가 모바일·검색·프로필과 다 다르게 보였다(디자인 기준 v2 · 목록 타일).
  if (!isHotpleLanding) {
    const subtitleParts = [place?.type, place?.subcategory].filter(Boolean);
    if (!isNaN(Number(place?.distanceFromMe))) {
      subtitleParts.push(formatDistance(Number(place?.distanceFromMe)));
    }

    return (
      <div ref={tileRef}>
        <ListTile
          verified={Boolean(place?.storeAccountVerifiedAt)}
          imageUrl={shouldShowTileImage && !tileImageError ? tileImageSrc : ""}
          title={place?.name}
          subtitle={subtitleParts.join(" · ")}
          meta={
            hotpleReviewCount > 0 ? (
              <TileRating rating={hotpleAverageRating} reviews={hotpleReviewCount} />
            ) : externalRating > 0 ? (
              <TileRating rating={externalRating} source="네이버" />
            ) : null
          }
          onClick={handleTileClick}
          active={isSelected}
          right={
            closureRiskLabel ? (
              <span className="shrink-0 rounded-chip border border-hairline px-1.5 py-0.5 text-t-label text-ink-45">
                {closureRiskLabel}
              </span>
            ) : null
          }
          className="px-4"
        />
      </div>
    );
  }

  return (
    <div
      className={`flex gap-2 ${tileLayoutClass} ${tileStateClass} transition duration-200 ease-in-out cursor-pointer overflow-x-hidden`}
      ref={tileRef}
      onClick={handleTileClick}
    >
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        {isHotpleLanding ? (
          <div className="flex items-start gap-2">
            <span className="mt-0.5 flex h-5 min-w-5 items-center justify-center rounded bg-light_bg_grey text-[11px] font-bold text-black/55 dark:bg-dark_bg_grey dark:text-white/65">
              {i + 1}
            </span>
            <span className={titleClassName}>
              {searchPlace && searchPlace?.length > 0
                ? renderHighlightedText(place?.name, searchPlace, "")
                : place?.name}
              {closureRiskLabel && (
                <span
                  className={`ml-2 inline-flex align-middle rounded border px-1.5 py-0.5 text-[11px] font-semibold leading-none ${closureRiskClass}`}
                >
                  {closureRiskLabel}
                </span>
              )}
            </span>
          </div>
        ) : searchPlace && searchPlace?.length > 0 ? (
          <span className={titleClassName}>
            {renderHighlightedText(place?.name, searchPlace, "")}
            {closureRiskLabel && (
              <span
                className={`ml-2 inline-flex align-middle rounded border px-1.5 py-0.5 text-[11px] font-semibold leading-none ${closureRiskClass}`}
              >
                {closureRiskLabel}
              </span>
            )}
          </span>
        ) : (
          <span className={titleClassName}>
            {place?.name}
            {closureRiskLabel && (
              <span
                className={`ml-2 inline-flex align-middle rounded border px-1.5 py-0.5 text-[11px] font-semibold leading-none ${closureRiskClass}`}
              >
                {closureRiskLabel}
              </span>
            )}
          </span>
        )}
        {closureRisk?.publicWarning && (
          <span className="max-w-[250px] truncate text-[11px] font-medium text-orange-700 dark:text-orange-100">
            {"\uBC29\uBB38 \uC804 \uC601\uC5C5 \uC5EC\uBD80 \uD655\uC778 \uD544\uC694"}
          </span>
        )}
        {hotpleStatsLabel && (
          <Span className="!text-[11px] !font-semibold tracking-tight !text-black/60 dark:!text-white/65 text-start">
            {hotpleStatsLabel}
          </Span>
        )}

        {/* 평점 표시.
            예전에는 출처를 가리지 않고 별 다섯 칸을 그리고 그 옆에 핫플 리뷰 수를 붙였다.
            그래서 네이버 평점 4.61 을 가진 가게가 "★★★★★ (0)" 으로 보였다 —
            평점 만점인데 리뷰가 0개인 것처럼 읽힌다. 별과 숫자의 출처가 달랐던 것이다.
            이제 어느 쪽 평점인지 밝히고, 핫플 평가가 생기면 핫플 것으로 바꾼다. */}
        {isHotpleLanding ? (
          <div className="flex items-center gap-0.5">
            <Rating
              name="read-only"
              value={Number(ratingValue || 0)}
              size="small"
              icon={<StarIcon sx={{ fontSize: 15, color: "gold" }} />}
              emptyIcon={
                <StarIcon
                  sx={{
                    fontSize: 15,
                    color: theme == "dark" ? "rgb(255 255 255 / 0.5)" : "inherit",
                  }}
                />
              }
              readOnly
            />
            <Span>{ratingLabel}</Span>
          </div>
        ) : hotpleReviewCount > 0 ? (
          <div className="flex items-center gap-1">
            <StarIcon sx={{ fontSize: 15, color: "gold" }} />
            <Span>
              {formatRating(hotpleAverageRating)}
              <span className="ml-1 text-black/45 dark:text-white/45">
                리뷰 {Number(hotpleReviewCount).toFixed(0)}
              </span>
            </Span>
          </div>
        ) : externalRating > 0 ? (
          <div className="flex items-center gap-1">
            <StarIcon sx={{ fontSize: 15, color: "gold" }} />
            <Span>
              {formatRating(externalRating)}
              <span className="ml-1 text-black/45 dark:text-white/45">네이버</span>
            </Span>
          </div>
        ) : null}
        {isHotpleLanding && placeMeta ? (
          <Span className="!text-[11px] tracking-tight !text-black/55 dark:!text-white/65 text-start line-clamp-2">
            {placeMeta}
          </Span>
        ) : place?.type && (
          <Span className="!text-xs tracking-tight !text-black/50 dark:!text-white/70 text-nowrap text-start">
            {place?.type}
            {place?.subcategory && `,${place?.subcategory}`}
            {!isNaN(Number(place?.distanceFromMe)) && (
              <span className="text-inherit">
                {"  "}| {formatDistance(Number(place?.distanceFromMe))}
              </span>
            )}
          </Span>
        )}
        {!isHotpleLanding && place?.amenities && (
          <Span className="!text-[11px] tracking-tight max-w-[170px] sm:max-w-[250px] truncate !text-black/40 dark:!text-white/60 text-nowrap text-start">
            {place?.amenities}
          </Span>
        )}
        {!isHotpleLanding && place?.description && (
          <Span className={"!text-sm tracking-tighter mt-2"}>
            {place?.description}
          </Span>
        )}
        {isHotpleLanding ? (
          <p className="mt-2 w-full rounded-md bg-light_bg_grey px-2.5 py-2 text-xs font-semibold leading-relaxed tracking-normal text-black/75 line-clamp-3 dark:bg-dark_bg_grey dark:text-white/75">
            "{reviewSnippet}"
          </p>
        ) : placeReviewText ? (
          <p
            className="w-full max-w-[170px] sm:max-w-[250px] truncate text-nowrap text-xs tracking-tight leading-snug font-semibold"
          >
            {placeReviewText}
          </p>
        ) : null}
        {isHotpleLanding ? (
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold text-black/60 dark:text-white/65">
            <span className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-2 py-1 dark:border-white/10 dark:bg-transparent">
              <Iconify icon="mdi:star-outline" />
              방문 리뷰 {formatCount(reviewCount)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-black/[0.02] px-2 py-1 text-black/60 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/65">
              <Iconify icon="mdi:star-check-outline" />
              {hotpleReviewCount > 0
                ? `Hotple ${formatRating(hotpleAverageRating)} · 평가 ${formatCount(hotpleReviewCount)}`
                : "Hotple 평가하기"}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-2 py-1 dark:border-white/10 dark:bg-transparent">
              <Iconify icon="mdi:play-circle-outline" />
              {playCount > 0 ? `Play ${playCount}` : "Play"}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-2 py-1 dark:border-white/10 dark:bg-transparent">
              <Iconify icon="mdi:heart-outline" />
              {saveCount > 0 ? `저장 ${saveCount}` : "저장"}
            </span>
          </div>
        ) : null}
        {isHotpleLanding ? (
          <div className="mt-3 flex items-center justify-between gap-2 border-t border-black/10 pt-2 dark:border-white/10">
            <button
              className="inline-flex items-center gap-1 text-xs font-bold text-red hover:underline"
              onClick={handleDetailActionClick}
              type="button"
            >
              상세 보기
              <Iconify icon="mdi:chevron-right" />
            </button>
            <button
              className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-black/70 hover:bg-black/[0.04] dark:border-white/10 dark:bg-transparent dark:text-white/70 dark:hover:bg-white/[0.06]"
              onClick={handleMapActionClick}
              type="button"
            >
              <Iconify icon="mdi:map-marker-outline" />
              지도에서 보기
            </button>
          </div>
        ) : null}
      </div>
      {placeMediaLoader ? (
        <div className={`${mediaFrameClass} animate-pulse`} />
      ) : (
        <>
          {shouldShowTileImage && (
            <div className={mediaFrameClass}>
              <Image
                key={`${placeId || place?._id}_image`}
                src={tileImageSrc}
                fill
                sizes="96px"
                alt={place?.name || "place image"}
                loading={loadMediaImmediately ? "eager" : "lazy"}
                draggable={false}
                className="object-cover"
                onError={() => setTileImageError(true)}
              />
            </div>
          )}
          {hasPlaceMedia && !shouldRenderMedia && (
            <div className={mediaFrameClass} />
          )}
          {!shouldShowTileImage && (!hasPlaceMedia || shouldRenderMedia) && (
            <PlaceTileMediaFallback className={mediaFrameClass} />
          )}
        </>
      )}
    </div>
  );
};

export default PlaceTile;
